import { useEffect, useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import styles from './AgreementPopup.module.css';

interface AgreementPopupProps {
  isOpen: boolean;
  onClose: () => void;
  companyName: string;
}

export const AgreementPopup = ({ isOpen, onClose, companyName }: AgreementPopupProps) => {
  const { t, selectedLocale } = useTranslation();
  const [content, setContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      fetchMarkdownContent();
    }
  }, [isOpen]);

  const fetchMarkdownContent = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch('/rights_and_agreements/rights_and_agreements.md');

      if (!response.ok) {
        throw new Error('Failed to load agreement content');
      }

      const text = await response.text();
      const processedText = text.replace(/{PARTY_NAME}/g, companyName);
      setContent(processedText);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load content');
    } finally {
      setIsLoading(false);
    }
  };

  const parseMarkdown = (markdown: string): string => {
    let html = markdown;

    // Headers
    html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
    html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
    html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');

    // Bold
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    // Italic
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');

    // Links
    html = html.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

    // Lists
    html = html.replace(/^\* (.*$)/gim, '<li>$1</li>');
    html = html.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');

    // Line breaks
    html = html.replace(/\n\n/g, '</p><p>');
    html = '<p>' + html + '</p>';

    // Clean up empty paragraphs
    html = html.replace(/<p><\/p>/g, '');
    html = html.replace(/<p>(<h[1-3]>)/g, '$1');
    html = html.replace(/(<\/h[1-3]>)<\/p>/g, '$1');
    html = html.replace(/<p>(<ul>)/g, '$1');
    html = html.replace(/(<\/ul>)<\/p>/g, '$1');

    return html;
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.container}>
        <button className={styles.close_button} onClick={onClose} aria-label="Close">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        <div className={styles.content}>
          {isLoading && (
            <div className={styles.loading_state}>
              <p>{t('agreementLoading', selectedLocale)}</p>
            </div>
          )}

          {error && (
            <div className={styles.error_state}>
              <p>{t('agreementError', selectedLocale)}: {error}</p>
              <button onClick={fetchMarkdownContent} className={styles.retry_button}>
                {t('retry', selectedLocale)}
              </button>
            </div>
          )}

          {!isLoading && !error && content && (
            <div
              className={styles.markdown_content}
              dangerouslySetInnerHTML={{ __html: parseMarkdown(content) }}
            />
          )}
        </div>
      </div>
    </div>
  );
};
